import React from 'react';
import {
  BffConfigurationResponse,
  DevConfig,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {GfaReferenceDisclaimerKindV1} from '@volkswagen-onehub/gfa-disclaimer-manager';
import {SimulatorFeatureAppContentConfig} from '../types';

export type ExternallyRenderedDisclaimerKind = GfaReferenceDisclaimerKindV1;

export type ConfigurationsContextValue = {
  bffData: BffConfigurationResponse<unknown, unknown, unknown>;
  appDataContent: SimulatorFeatureAppContentConfig | undefined;
  externallyRenderedDisclaimerKind: ExternallyRenderedDisclaimerKind;
  devConfig: DevConfig;
};

const ConfigurationsContext =
  React.createContext<ConfigurationsContextValue | null>(null);

type ConfigurationsContextWrapperProps<
  AppData extends BffConfigurationResponse<unknown, unknown, unknown>
> = {
  appDataPromise: Promise<AppData>;
  appDataContent: SimulatorFeatureAppContentConfig | undefined;
  children: React.ReactNode;
  externallyRenderedDisclaimerKind: ExternallyRenderedDisclaimerKind;
  devConfig: DevConfig;
};

export function ConfigurationsContextProvider<
  AppData extends BffConfigurationResponse<unknown, unknown, unknown>
>({
  appDataPromise,
  appDataContent,
  children,
  externallyRenderedDisclaimerKind,
  devConfig,
}: ConfigurationsContextWrapperProps<AppData>): JSX.Element | null {
  const [data, setData] = React.useState<undefined | AppData>();

  React.useEffect(() => {
    appDataPromise?.then(setData);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- This should only happen once
  }, []);

  const contextValue: null | ConfigurationsContextValue = React.useMemo(() => {
    if (data === undefined) {
      return null;
    }
    return {
      bffData: data,
      appDataContent,
      externallyRenderedDisclaimerKind,
      devConfig,
    };
  }, [data, appDataContent, externallyRenderedDisclaimerKind, devConfig]);

  if (data === undefined) {
    return null;
  }

  return (
    <ConfigurationsContext.Provider value={contextValue}>
      {children}
    </ConfigurationsContext.Provider>
  );
}

export function useConfigurationsContext(): ConfigurationsContextValue {
  const value = React.useContext(ConfigurationsContext);

  if (value === null) {
    throw new Error(
      'configurations context was either accessed outside of <ConfigurationContextProvider>, or inside even though the config was not loaded yet (which the ConfigurationContext should prevent from happening)'
    );
  }

  return value;
}

export function useConfiguration<
  Configs extends BffConfigurationResponse<unknown, unknown, unknown>
>(): Configs {
  return useConfigurationsContext().bffData as Configs;
}
